import { Alert, Button, Grid, IconButton, Typography } from "@mui/material";
import { readLiquidationDetails } from "api/liquidations";
import AdditionalDetailTable from "components/Liquidations/LiquidationDetail/DetailTables/Aditionals/AdditionalsDetailTable";
import DischargeDetailTable from "components/Liquidations/LiquidationDetail/DetailTables/IncomeDischarges/DischargeDetailTable";
import IncomeDetailTable from "components/Liquidations/LiquidationDetail/DetailTables/IncomeDischarges/IncomeDetailTable";
import StayDetailTable from "components/Liquidations/LiquidationDetail/DetailTables/Stay/StayDetailTable";
import PageLayout from "components/layout/PageLayout";
import Loader from "components/lists/Loader";
import { LIQUIDATIONS_URL } from "constants/urls";
import { STATUS_CODES } from "constants/index";
import useCrud from "hooks/useCrud";
import { createRef, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import type { FileDownloadResponse, LiquidationAggregation } from "types";

import LiquidationDetailsCard from "components/Liquidations/LiquidationDetail/LiquidationDetailsCard";
import TotalCards from "components/Liquidations/LiquidationDetail/TotalCards";
import { PrintLayout } from "components/Prints/PrintLayout";
import { MdDownload, MdPrint } from "react-icons/md";
import { chipHandler } from "components/utils/LiquidationDetails";
import { exportLiquidationToExcel } from "helpers/exportToExcel";
import DetailConfirmationModal from "components/utils/DetailConfirmationModal";
import { LiquidationPrintLayout } from "components/Liquidations/LiquidationDetail/LiquidationPrintLayout";
import { formatLiquidationNo } from "helpers/liquidations";

export const LiquidationDetailsPage = () => {
  const { id } = useParams();

  const printRef: React.RefObject<HTMLDivElement> = createRef<HTMLDivElement>();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    copyStyles: true,
  });

  const printRefDetail: React.RefObject<HTMLDivElement> =
    createRef<HTMLDivElement>();

  const handlePrintDetail = useReactToPrint({
    content: () => printRefDetail.current,
    copyStyles: true,
  });

  const { fetchData } = useCrud<FileDownloadResponse>(LIQUIDATIONS_URL, { disableAutoFetch: true });
  const fetchDataRef = useRef(fetchData);
  const loadAttemptRef = useRef<symbol | null>(null);
  const [liquidations, setLiquidations] = useState<LiquidationAggregation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [isPrinting, setIsPrinting] = useState<boolean>(false);

  const [confirmExportOpen, setConfirmExportOpen] = useState<boolean>(false);

  useEffect(() => {
    fetchDataRef.current = fetchData;
  }, [fetchData]);

  const refetchLiquidation = useCallback(async () => {
    const attempt = Symbol();
    loadAttemptRef.current = attempt;
    setLoading(true);
    setError(null);
    setLiquidations([]);
    setConfirmExportOpen(false);

    try {
      const liquidationId = Number(id);
      if (!Number.isSafeInteger(liquidationId) || liquidationId <= 0) {
        throw new Error("El identificador de la liquidacion no es valido.");
      }

      const response = await fetchDataRef.current(String(liquidationId), true);
      if (loadAttemptRef.current !== attempt) return;
      if (response.status !== STATUS_CODES.OK) {
        const message = response.data?.message;
        throw new Error(typeof message === "string" && message.trim()
          ? message : "No se pudo obtener el detalle de la liquidacion.");
      }

      const detail = await readLiquidationDetails(response.data, liquidationId);
      if (loadAttemptRef.current === attempt) setLiquidations(detail);
    } catch (loadError) {
      if (loadAttemptRef.current === attempt) {
        setError(loadError instanceof Error ? loadError.message : "No se pudo cargar la liquidacion.");
      }
    } finally {
      if (loadAttemptRef.current === attempt) {
        setLoading(false);
        loadAttemptRef.current = null;
      }
    }
  }, [id]);

  useEffect(() => {
    void refetchLiquidation();
    return () => { loadAttemptRef.current = null; };
  }, [refetchLiquidation]);

  const liquidation = liquidations[0]?.id === Number(id) ? liquidations[0] : undefined;

  const allLiquidations = useMemo(() => {
    if (!liquidation) return [];
    return [liquidation, ...(liquidation.child_liquidations ?? [])];
  }, [liquidation]);

  const liquidationsToRender = useMemo(() => {
    if (!liquidation) return [];

    const hasChildLiquidations = (liquidation.child_liquidations?.length ?? 0) > 0;

    if (hasChildLiquidations) {
      return liquidation.child_liquidations ?? [];
    }

    return [liquidation];
  }, [liquidation]);

  const consolidatedLiquidation = useMemo(() => {
    if (!liquidation) return null;

    return {
      ...liquidation,
      liquidation_settlement_types: [
        {
          ...liquidation.liquidation_settlement_types[0],
          subtotal: allLiquidations.reduce(
            (acc, liq) => acc + (liq.liquidation_settlement_types?.[0]?.subtotal ?? 0),
            0
          ),
        },
        {
          ...liquidation.liquidation_settlement_types[1],
          subtotal: allLiquidations.reduce(
            (acc, liq) => acc + (liq.liquidation_settlement_types?.[1]?.subtotal ?? 0),
            0
          ),
        },
        {
          ...liquidation.liquidation_settlement_types[2],
          subtotal: allLiquidations.reduce(
            (acc, liq) => acc + (liq.liquidation_settlement_types?.[2]?.subtotal ?? 0),
            0
          ),
        },
      ],
      subtotal_adicionales: allLiquidations.reduce(
        (acc, liq) => acc + (liq.subtotal_adicionales ?? 0),
        0
      ),
      total: allLiquidations.reduce(
        (acc, liq) => acc + (liq.total ?? 0),
        0
      ),
    };
  }, [liquidation, allLiquidations]);

  return (
    <PageLayout
      pageTitle="Detalle de Liquidación"
      pageDesc="Liquidaciones de ingresos, estadías, egresos y adicionales"
    >
      {liquidation && consolidatedLiquidation && (
        <>
          <PrintLayout
            ref={printRef}
            headerInfo={
              <LiquidationDetailsCard
                id={formatLiquidationNo(consolidatedLiquidation)}
                liquidation={consolidatedLiquidation}
              />
            }
          >
            <LiquidationPrintLayout
              consolidatedLiquidation={consolidatedLiquidation}
              liquidationsToPrint={liquidationsToRender}
              hasDetail={false}
            />
          </PrintLayout>

          <PrintLayout
            ref={printRefDetail}
            headerInfo={
              <LiquidationDetailsCard
                id={formatLiquidationNo(consolidatedLiquidation)}
                liquidation={consolidatedLiquidation}
              />
            }
          >
            <LiquidationPrintLayout
              consolidatedLiquidation={consolidatedLiquidation}
              liquidationsToPrint={liquidationsToRender}
              hasDetail={true}
            />
          </PrintLayout>
        </>
      )}
      {error ? (
        <Alert
          severity="error"
          action={<Button color="inherit" onClick={() => void refetchLiquidation()}>Reintentar</Button>}
        >
          {error}
        </Alert>
      ) : loading || !liquidation ? (
        <Loader isLoading={loading} isFirstLoad={false} />
      ) : (
        <>
          <Grid container spacing={2} paddingBottom={10}>
            <Grid
              item
              xs={12}
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "flex-start",
                displayPrint: "none",
              }}
            >
              <IconButton
                size="medium"
                onClick={() => {
                  setIsPrinting(true);
                  setConfirmExportOpen(true);
                }}
              >
                <MdPrint />
              </IconButton>
              <IconButton
                size="medium"
                onClick={() => {
                  setIsPrinting(false);
                  setConfirmExportOpen(true);
                }}
              >
                <MdDownload />
              </IconButton>
            </Grid>
            <Grid item xs={12}>
              {consolidatedLiquidation && (
                <TotalCards liquidation={consolidatedLiquidation} />
              )}
            </Grid>
            <Grid item xs={12}>
              <Grid container spacing={3}>
                {liquidationsToRender.map((currentLiquidation, index) => {
                  const isParentLiquidation =
                    currentLiquidation.parent_liquidation_id === null ||
                    currentLiquidation.parent_liquidation_id === undefined;

                  const currentClient = currentLiquidation.liquidation_clients?.[0];
                  return (
                    <Grid item xs={12} key={currentLiquidation.id}>
                      <Grid container spacing={2}>
                        <Grid item xs={12}>
                          <Typography variant="h6">
                            {isParentLiquidation
                              ? `Liquidación Padre - ${currentClient?.razon_social ?? "-"}`
                              : `Liquidación Línea - ${currentClient?.razon_social ?? "-"}`}
                          </Typography>
                        </Grid>

                        <Grid item xs={12}>
                          <LiquidationDetailsCard
                            liquidation={currentLiquidation}
                            incomeChip={chipHandler(
                              currentLiquidation.liquidation_settlement_types[0]
                                .settlement_type,
                            )}
                            stayChip={chipHandler(
                              currentLiquidation.liquidation_settlement_types[1]
                                .settlement_type,
                            )}
                            dischargeChip={chipHandler(
                              currentLiquidation.liquidation_settlement_types[2]
                                .settlement_type,
                            )}
                            id={formatLiquidationNo(currentLiquidation, index)}
                          />
                        </Grid>

                        <Grid item xs={12}>
                          <Grid container spacing={1} columnSpacing={2}>
                            {currentLiquidation.liquidation_settlement_types[0].subtotal > 0 && (
                              <IncomeDetailTable
                                liquidation={currentLiquidation}
                                chip={chipHandler(
                                  currentLiquidation.liquidation_settlement_types[0]
                                    .settlement_type,
                                )}
                                hasDetail
                              />
                            )}

                            {currentLiquidation.liquidation_settlement_types[1].subtotal > 0 && (
                              <StayDetailTable
                                liquidation={currentLiquidation}
                                chip={chipHandler(
                                  currentLiquidation.liquidation_settlement_types[1]
                                    .settlement_type,
                                )}
                                hasDetail
                              />
                            )}

                            {currentLiquidation.liquidation_settlement_types[2].subtotal > 0 && (
                              <DischargeDetailTable
                                liquidation={currentLiquidation}
                                chip={chipHandler(
                                  currentLiquidation.liquidation_settlement_types[2]
                                    .settlement_type,
                                )}
                                hasDetail
                              />
                            )}

                            {currentLiquidation.subtotal_adicionales > 0 && (
                              <AdditionalDetailTable
                                liquidation={currentLiquidation}
                                hasDetail
                                refetchLiquidation={() => refetchLiquidation()}
                              />
                            )}
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  );
                })}
              </Grid>
            </Grid>
          </Grid>
          <DetailConfirmationModal
            open={confirmExportOpen}
            handleOpen={() => setConfirmExportOpen(!confirmExportOpen)}
            handleConfirm={(hasDetail) => {
              if (isPrinting) {
                if (hasDetail) {
                  handlePrintDetail();
                } else {
                  handlePrint();
                }
              } else exportLiquidationToExcel(liquidation, hasDetail);
            }}
          />
        </>
      )}
    </PageLayout>
  );
};
